import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import type { LorawanApplication, LorawanDevice } from '../api/types.js';
import { useApi, useAuth } from '../auth.js';
import { isPlatform } from '../lib/permissions.js';
import { Age, ErrorBox, Spinner } from '../components.js';
import { useNow, useResource } from '../hooks.js';
import { DEFAULT_TIMEZONE, formatCount, formatFloat, PLACEHOLDER } from '../lib/format.js';
import {
  applicationChoice,
  batteryLevel,
  deviceState,
  formatDevEui,
  signalLevel,
  sortDevices,
  type Level,
} from '../lib/lorawan.js';
import { GatewaysTab } from './LorawanGateways.js';
import { SensorForm, SensorImport } from './LorawanSensors.js';

export const LORAWAN_REFRESH_MS = 30_000;

const LEVEL_CLASS: Record<Level, string | undefined> = { ok: undefined, warn: 'warn', bad: 'bad' };

function levelClass(l: Level | null): string | undefined {
  return l ? LEVEL_CLASS[l] : undefined;
}

function num(v: number | null, unit: string): string {
  return v === null ? PLACEHOLDER : `${formatFloat(v)} ${unit}`;
}

interface RowActions {
  busy: boolean;
  onEdit: (d: LorawanDevice) => void;
  onToggle: (d: LorawanDevice) => void;
  onDelete: (d: LorawanDevice) => void;
}

function DeviceRow({
  d,
  siteId,
  edgeId,
  now,
  timeZone,
  actions,
}: {
  d: LorawanDevice;
  siteId: string;
  edgeId: string | null;
  now: number;
  timeZone: string;
  /** Null = ChirpStack not configured (no management). */
  actions: RowActions | null;
}) {
  const signal = signalLevel(d.rssi, d.snr);
  const state = deviceState(d);
  const manageable = actions !== null && d.in_chirpstack === true;
  return (
    <tr>
      <td>
        <div className="strong">{d.name ?? PLACEHOLDER}</div>
        <div className="muted small mono">{formatDevEui(d.dev_eui)}</div>
        {d.description && <div className="muted small">{d.description}</div>}
      </td>
      <td>{d.profile ?? PLACEHOLDER}</td>
      <td className={levelClass(state.level)}>{state.label}</td>
      <td>
        <Age ts={d.last_uplink} now={now} timeZone={timeZone} />
        {d.in_chirpstack === false && <div className="warn small">not in ChirpStack</div>}
      </td>
      <td className={`num ${levelClass(batteryLevel(d.battery)) ?? ''}`}>{num(d.battery, '%')}</td>
      <td className={`num ${levelClass(signal) ?? ''}`}>{num(d.rssi, 'dBm')}</td>
      <td className={`num ${levelClass(signal) ?? ''}`}>{num(d.snr, 'dB')}</td>
      <td className="num">
        {d.id && edgeId ? (
          <Link
            to={`/points?site_id=${encodeURIComponent(siteId)}&edge_id=${encodeURIComponent(edgeId)}&q=${encodeURIComponent(`lora-${d.dev_eui}`)}`}
          >
            {formatCount(d.points)}
          </Link>
        ) : (
          <span className="muted" title="Points appear with the first uplink">
            {formatCount(d.points)}
          </span>
        )}
      </td>
      {actions && (
        <td>
          {manageable && (
            <div className="row-actions">
              <button
                type="button"
                className="btn btn-small"
                disabled={actions.busy}
                onClick={() => {
                  actions.onEdit(d);
                }}
              >
                Edit
              </button>
              <button
                type="button"
                className="btn btn-small"
                disabled={actions.busy}
                onClick={() => {
                  actions.onToggle(d);
                }}
              >
                {d.enabled === false ? 'Enable' : 'Disable'}
              </button>
              <button
                type="button"
                className="btn btn-small"
                disabled={actions.busy}
                onClick={() => {
                  actions.onDelete(d);
                }}
              >
                Delete
              </button>
            </div>
          )}
        </td>
      )}
    </tr>
  );
}

function MappingSection({
  siteId,
  siteName,
  mapped,
  onChanged,
}: {
  siteId: string;
  siteName: string;
  mapped: LorawanApplication[];
  onChanged: () => void;
}) {
  const api = useApi();
  const apps = useResource('lorawan-applications', (s) => api.lorawanApplications(s));
  const [selected, setSelected] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const choice = applicationChoice(apps.data?.items ?? [], siteId);
  const here = apps.data ? choice.here : mapped;

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      setSelected('');
      apps.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setBusy(false);
    }
  }

  function map() {
    const app = [...choice.free, ...choice.elsewhere].find((a) => a.application_id === selected);
    if (!app) return;
    if (
      app.site &&
      !window.confirm(
        `“${app.name}” belongs to ${app.site.name}. Move it to ${siteName}? New uplinks are then stored here.`,
      )
    ) {
      return;
    }
    void run(() => api.mapLorawanApplication(app.application_id, siteId));
  }

  function unmap(app: LorawanApplication) {
    if (
      !window.confirm(
        `Remove “${app.name}” from ${siteName}? Its uplinks are dropped until it is mapped again; stored data stays.`,
      )
    ) {
      return;
    }
    void run(() => api.unmapLorawanApplication(app.application_id));
  }

  return (
    <>
      <div className="section-head">
        <h2>ChirpStack applications</h2>
        <span className="muted small">
          Uplinks of a mapped application become points of this site
        </span>
      </div>
      {error && <ErrorBox error={error} />}
      {apps.data?.chirpstack_error && (
        <p className="warn small">ChirpStack: {apps.data.chirpstack_error}</p>
      )}
      {apps.error && <ErrorBox error={apps.error} onRetry={apps.reload} />}
      {here.length === 0 ? (
        <p className="empty">No application is mapped to this site.</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Application</th>
                <th>ChirpStack tenant</th>
                <th>
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {here.map((a) => (
                <tr key={a.application_id}>
                  <td>
                    <div className="strong">{a.name}</div>
                    <div className="muted small mono">{a.application_id}</div>
                    {a.in_chirpstack === false && (
                      <div className="warn small">ChirpStack no longer lists this application</div>
                    )}
                  </td>
                  <td>{a.tenant_name ?? PLACEHOLDER}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-small"
                      disabled={busy}
                      onClick={() => {
                        unmap(a);
                      }}
                    >
                      Unmap
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {apps.data?.chirpstack && (
        <form
          className="toolbar"
          onSubmit={(e) => {
            e.preventDefault();
            map();
          }}
        >
          <label>
            Map application{' '}
            <select
              value={selected}
              disabled={busy}
              onChange={(e) => {
                setSelected(e.target.value);
              }}
            >
              <option value="">Choose…</option>
              {choice.free.length > 0 && (
                <optgroup label="Not mapped">
                  {choice.free.map((a) => (
                    <option key={a.application_id} value={a.application_id}>
                      {a.name}
                      {a.tenant_name ? ` (${a.tenant_name})` : ''}
                    </option>
                  ))}
                </optgroup>
              )}
              {choice.elsewhere.length > 0 && (
                <optgroup label="Mapped to another site">
                  {choice.elsewhere.map((a) => (
                    <option key={a.application_id} value={a.application_id}>
                      {a.name} → {a.site?.name}
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
          </label>{' '}
          <button type="submit" className="btn" disabled={busy || selected === ''}>
            Map to {siteName}
          </button>
        </form>
      )}
      {apps.data && !apps.data.chirpstack && (
        <p className="muted small">
          The ChirpStack API is not configured (KALVIO_CHIRPSTACK__API_URL / __API_KEY), so
          applications cannot be listed here.
        </p>
      )}
    </>
  );
}

type Tab = 'sensors' | 'gateways';

/** LoRaWAN of a site: sensors (add, import, edit, disable, delete) and gateways, through ChirpStack. */
export function LorawanPage() {
  const { siteId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const tab: Tab = params.get('tab') === 'gateways' ? 'gateways' : 'sensors';
  const api = useApi();
  const platform = isPlatform(useAuth().user);
  const now = useNow(5000);
  const sites = useResource('sites', () => api.sites());
  const lora = useResource(
    `lorawan:${siteId}`,
    (s) => api.siteLorawan(siteId, s),
    LORAWAN_REFRESH_MS,
  );
  const site = sites.data?.find((s) => s.id === siteId);
  const tz = site?.timezone ?? DEFAULT_TIMEZONE;
  const devices = lora.data ? sortDevices(lora.data.devices) : [];
  const manage = lora.data?.chirpstack === true;
  const [panel, setPanel] = useState<'add' | 'import' | LorawanDevice | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  async function act(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
      lora.reload();
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setBusy(false);
    }
  }

  const actions: RowActions | null = manage
    ? {
        busy,
        onEdit: (d) => {
          setPanel(d);
        },
        onToggle: (d) => {
          const enable = d.enabled === false;
          if (
            !enable &&
            !window.confirm(
              `Disable “${d.name ?? d.dev_eui}”? ChirpStack ignores its uplinks until it is enabled again.`,
            )
          ) {
            return;
          }
          void act(() => api.updateLorawanDevice(siteId, d.dev_eui, { enabled: enable }));
        },
        onDelete: (d) => {
          if (
            !window.confirm(
              `Delete “${d.name ?? d.dev_eui}” (${d.dev_eui}) in ChirpStack? The sensor can no longer join; its stored points and history stay in Kalvio. To use it again, add it with its keys.`,
            )
          ) {
            return;
          }
          void act(() => api.deleteLorawanDevice(siteId, d.dev_eui));
        },
      }
    : null;

  const closePanel = () => {
    setPanel(null);
  };
  const panelDone = () => {
    setPanel(null);
    lora.reload();
  };

  return (
    <section>
      <div className="breadcrumbs">
        <Link to="/sites">Sites</Link> <span aria-hidden="true">/</span>{' '}
        <Link to={`/sites/${encodeURIComponent(siteId)}`}>{site?.name ?? 'Site'}</Link>{' '}
        <span aria-hidden="true">/</span>
      </div>
      <div className="page-head">
        <div>
          <h1>LoRaWAN</h1>
          <div className="muted small">
            Sensors through ChirpStack; each device is <code>lora-&lt;DevEUI&gt;</code> with its
            decoded fields and radio data as points.
          </div>
        </div>
        <div className="segmented" role="tablist" aria-label="LoRaWAN">
          {(
            [
              ['sensors', 'Sensors'],
              ['gateways', 'Gateways'],
            ] as const
          ).map(([t, label]) => (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={tab === t}
              className={tab === t ? 'active' : undefined}
              onClick={() => {
                setParams(t === 'sensors' ? {} : { tab: t }, { replace: true });
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {lora.data && !lora.data.chirpstack && platform && (
        <p className="muted small">
          The ChirpStack API is not configured (KALVIO_CHIRPSTACK__API_URL / __API_KEY): sensors and
          gateways cannot be managed here.
        </p>
      )}

      {tab === 'gateways' ? (
        manage ? (
          <GatewaysTab siteId={siteId} now={now} timeZone={tz} />
        ) : (
          !lora.data && <Spinner />
        )
      ) : (
        <>
          <div className="section-head">
            <h2>Sensors</h2>
            <div className="page-actions">
              <span className="muted small">
                {lora.loading && lora.data
                  ? 'Refreshing…'
                  : `Refreshes every ${String(LORAWAN_REFRESH_MS / 1000)} s`}
              </span>
              {manage && (
                <>
                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={panel !== null}
                    onClick={() => {
                      setPanel('add');
                    }}
                  >
                    Add sensor
                  </button>
                  <button
                    type="button"
                    className="btn"
                    disabled={panel !== null}
                    onClick={() => {
                      setPanel('import');
                    }}
                  >
                    Import CSV
                  </button>
                </>
              )}
            </div>
          </div>
          {panel === 'add' && (
            <SensorForm siteId={siteId} onDone={panelDone} onCancel={closePanel} />
          )}
          {panel === 'import' && (
            <SensorImport
              siteId={siteId}
              existing={new Set(devices.map((d) => d.dev_eui))}
              onDone={lora.reload}
              onCancel={closePanel}
            />
          )}
          {panel !== null && typeof panel === 'object' && (
            <SensorForm
              key={panel.dev_eui}
              siteId={siteId}
              device={panel}
              onDone={panelDone}
              onCancel={closePanel}
            />
          )}
          {error && <ErrorBox error={error} />}
          {lora.error && <ErrorBox error={lora.error} onRetry={lora.reload} />}
          {!lora.data && lora.loading && <Spinner />}
          {lora.data?.chirpstack_error && (
            <p className="warn small">ChirpStack: {lora.data.chirpstack_error}</p>
          )}
          {lora.data && devices.length === 0 && (
            <p className="empty">
              {manage
                ? 'No LoRaWAN sensors yet. Add one with its DevEUI, JoinEUI and AppKey, or import a CSV.'
                : platform
                  ? 'No LoRaWAN devices yet. Map a ChirpStack application below; devices appear with their first uplink.'
                  : 'No LoRaWAN devices yet.'}
            </p>
          )}
          {devices.length > 0 && (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Device</th>
                    <th>Profile</th>
                    <th>State</th>
                    <th>Last uplink</th>
                    <th className="num" title="Battery level (device status), %">
                      Battery
                    </th>
                    <th className="num" title="Best gateway, last uplink">
                      RSSI
                    </th>
                    <th className="num" title="Best gateway, last uplink">
                      SNR
                    </th>
                    <th className="num">Points</th>
                    {actions && (
                      <th>
                        <span className="sr-only">Actions</span>
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {devices.map((d) => (
                    <DeviceRow
                      key={d.dev_eui}
                      d={d}
                      siteId={siteId}
                      edgeId={lora.data?.edge_id ?? null}
                      now={now}
                      timeZone={tz}
                      actions={actions}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {lora.data && platform && (
            <details className="config-history">
              <summary>ChirpStack applications (advanced)</summary>
              <MappingSection
                siteId={siteId}
                siteName={site?.name ?? 'this site'}
                mapped={lora.data.applications}
                onChanged={lora.reload}
              />
            </details>
          )}
        </>
      )}
    </section>
  );
}
